/**
 * PersonalizedRecommendations.js
 * 版本：V1.02 | 更新日期：2025-07-19
 * 功能：個人化推薦系統組件，提供基於機器學習的市場推薦
 * 關連檔案：App.js, Firebase Functions
 * 
 * 更新內容：
 * V1.02 (2025-07-19) - 智慧推薦區域化
 * - 根據夥伴區域權限篩選推薦市場
 * - 加入區域標示顯示
 * - 支援向後相容性（無權限設定的夥伴可看到所有推薦）
 * - 優化推薦演算法的區域偏好分析
 * 
 * V1.01 (2025-07-16) - 初始版本
 * - 基本智慧推薦功能
 * - 基於歷史偏好和天氣的推薦演算法
 */

import React, { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
// 【AI新增】區域權限系統導入
// Why: 支援智慧推薦的區域標示和權限篩選
import { getRegionDisplayName, getRegionIcon } from '../../utils/regionUtils';
import { getMarketRegion } from '../../utils/marketUtils';

const PersonalizedRecommendations = ({ currentUser, bookings, markets, functions, appId }) => {
    // ===========================================
    // --- 狀態管理 ---
    // ===========================================
    const [recommendations, setRecommendations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // ===========================================
    // --- 基礎推薦 (本地備案) ---
    // ===========================================
    // 【AI保證】當推薦服務失敗時，提供基礎推薦作為備案
    // Why: 確保用戶始終能獲得有用的推薦，提升系統可靠性
    const getBasicRecommendations = useCallback(() => {
        if (!currentUser || !markets.length) return [];
        
        const twoWeeksAgo = new Date();
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
        
        const userBookings = bookings.filter(b => b.vendorId === currentUser.id);
        const marketStats = userBookings.reduce((acc, b) => {
            if (!acc[b.marketId]) {
                acc[b.marketId] = { sales: 0, count: 0, lastBooked: null };
            }
            
            // 【AI修正】統一使用 salesDetails 計算總銷量，確保與前端業績查詢數據一致
            let bookingSales = 0;
            if (Array.isArray(b.salesDetails) && b.salesDetails.length > 0) {
                // 使用 salesDetails 計算總銷量
                b.salesDetails.forEach(item => {
                    bookingSales += Number(item.quantity) || 0;
                });
            } else if (b.salesQuantity) {
                // 如果沒有 salesDetails，則使用 salesQuantity
                bookingSales += Number(b.salesQuantity) || 0;
            }
            
            acc[b.marketId].sales += bookingSales;
            acc[b.marketId].count++;
            const bookingDate = new Date(b.date);
            if (!acc[b.marketId].lastBooked || bookingDate > acc[b.marketId].lastBooked) {
                acc[b.marketId].lastBooked = bookingDate;
            }
            return acc;
        }, {});

        // 【AI新增】根據夥伴區域權限篩選市場
        // Why: 確保基礎推薦也符合區域權限限制
        const filteredMarkets = markets.filter(market => {
            // 如果夥伴沒有區域權限設定，則可以推薦所有市場（向後相容性）
            if (!currentUser.regions || currentUser.regions.length === 0) {
                return true;
            }
            
            // 取得市場的區域分類
            const marketRegion = getMarketRegion(market);
            
            // 檢查市場是否在夥伴的權限區域內
            return currentUser.regions.includes(marketRegion);
        });

        const potentialMarkets = filteredMarkets
            .filter(m => !marketStats[m.id] || marketStats[m.id].lastBooked < twoWeeksAgo)
            .map(m => {
                const market = markets.find(mk => mk.id === m.id);
                return {
                    market,
                    score: 0.5,
                    confidence: 0.3,
                    explanations: ['基於時間的簡單推薦'],
                    predictedSuccess: 0.5,
                    reasons: ['basic']
                };
            })
            .slice(0, 5);

        return potentialMarkets;
    }, [currentUser, markets, bookings]);

    // ===========================================
    // --- 獲取個人化推薦 ---
    // ===========================================
    // 【AI承諾】此函數負責調用雲端推薦服務
    // Why: 利用雲端計算能力進行複雜的推薦演算法運算
    const fetchPersonalizedRecommendations = useCallback(async () => {
        if (!currentUser || !functions || !appId) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            console.log(`[智慧推薦] 開始為用戶 ${currentUser.id} 獲取推薦`);
            
            const getPersonalizedRecommendations = httpsCallable(functions, 'getPersonalizedRecommendations');
            const result = await getPersonalizedRecommendations({
                appId,
                vendorId: currentUser.id,
                limit: 5
            });

            if (result.data.success) {
                setRecommendations(result.data.recommendations || []);
                console.log(`[智慧推薦] 成功獲取 ${result.data.recommendations?.length || 0} 個推薦`);
            } else {
                console.error('[智慧推薦] 推薦獲取失敗:', result.data.error);
                setError(result.data.error || '獲取推薦失敗');
                // 降級到基礎推薦
                setRecommendations(getBasicRecommendations());
            }
        } catch (err) {
            console.error('[智慧推薦] 推薦獲取錯誤:', err);
            setError('推薦服務暫時不可用');
            // 使用本地基礎推薦
            setRecommendations(getBasicRecommendations());
        } finally {
            setIsLoading(false);
        }
    }, [currentUser, functions, appId, getBasicRecommendations]);

    // ===========================================
    // --- 生命週期管理 ---
    // ===========================================
    useEffect(() => {
        if (currentUser && functions && appId) {
            fetchPersonalizedRecommendations();
        }
    }, [currentUser, functions, appId, fetchPersonalizedRecommendations]);

    // ===========================================
    // --- 渲染邏輯 ---
    // ===========================================
    if (!currentUser) {
        return null; // 未登入不顯示推薦
    }

    // 【AI新增】提取市場名稱和區域標示用於顯示
    // Why: 提供更豐富的推薦資訊，包含區域標示
    const marketDisplayItems = recommendations
        .slice(0, 5)
        .map(rec => {
            const marketRegion = getMarketRegion(rec.market);
            const regionDisplay = getRegionDisplayName(marketRegion);
            const regionIcon = getRegionIcon(marketRegion);
            
            return {
                name: rec.market.name,
                region: regionDisplay,
                icon: regionIcon,
                score: rec.score
            };
        });

    return (
        <div className="mb-4">
            <div className="flex items-center gap-3 text-sm">
                <span className="font-bold text-gray-800">🎯 智慧推薦：</span>
                {isLoading ? (
                    <span className="text-gray-500">載入中...</span>
                ) : error ? (
                    <span className="text-orange-600">服務暫時不可用</span>
                ) : marketDisplayItems.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                        {marketDisplayItems.map((item, index) => (
                            <span key={index} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                <span>{item.icon}</span>
                                <span className="font-medium">{item.name}</span>
                                <span className="text-blue-600">({item.region})</span>
                            </span>
                        ))}
                    </div>
                ) : (
                    <span className="text-gray-500">暫無推薦</span>
                )}
            </div>
        </div>
    );
};

export default PersonalizedRecommendations;
